<script setup>
import { ref, onMounted } from 'vue'
import { usePagination } from '@/shared/composables/usePagination'
import Card from 'primevue/card'
import Button from 'primevue/button'
import { useRouter } from 'vue-router'
import Toast from 'primevue/toast'
import { useToast } from 'primevue/usetoast'

import { getAllRoles, deleteRole } from '../api/roles.service'
import RoleFormDialog from '../components/RoleFormDialog.vue'
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog.vue'

const router = useRouter()
const toast = useToast()

const roles = ref([])
const loading = ref(false)
const showRoleForm = ref(false)
const showDeleteDialog = ref(false)
const currentRole = ref(null)
const formMode = ref('create')
const deleteLoading = ref(false)

const PROTECTED_FROM_DELETE = ['God']

function canDeleteRole(roleName) {
  return !PROTECTED_FROM_DELETE.includes(roleName)
}

async function loadRoles() {
  try {
    loading.value = true
    roles.value = await getAllRoles()
  } catch (error) {
    console.error('Ошибка при загрузке ролей', error)
    roles.value = []
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error.response?.data?.message || error.message || 'Не удалось загрузить список ролей',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}

function goBack() {
  router.push('/admin')
}

function openRolePermissions(roleId) {
  router.push(`/admin/roles/${roleId}/permissions`)
}

function openCreateRole() {
  currentRole.value = null
  formMode.value = 'create'
  showRoleForm.value = true
}

function openEditRole(role) {
  currentRole.value = role
  formMode.value = 'edit'
  showRoleForm.value = true
}

function openDeleteDialog(role) {
  if (!canDeleteRole(role.name)) {
    toast.add({
      severity: 'warn',
      summary: 'Действие недоступно',
      detail: `Роль "${role.name}" является системной и не может быть удалена`,
      life: 3000
    })
    return
  }

  currentRole.value = role
  showDeleteDialog.value = true
}

function closeDeleteDialog() {
  if (deleteLoading.value) return
  showDeleteDialog.value = false
  currentRole.value = null
}

async function handleDeleteRole() {
  if (deleteLoading.value) return
  if (!currentRole.value) return

  deleteLoading.value = true
  try {
    await deleteRole(currentRole.value.id)
    toast.add({
      severity: 'success',
      summary: 'Успех',
      detail: `Роль "${currentRole.value.name}" успешно удалена`,
      life: 3000
    })
    await loadRoles()
  } catch (error) {
    console.error('Ошибка при удалении роли', error)
    const errorMessage = error.response?.data?.message || 'Не удалось удалить роль'
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: errorMessage,
      life: 5000
    })
  } finally {
    deleteLoading.value = false
    closeDeleteDialog()
  }
}

function handleRoleSaved() {
  toast.add({
    severity: 'success',
    summary: 'Успех',
    detail: formMode.value === 'create' ? 'Роль успешно создана' : 'Роль успешно обновлена',
    life: 3000
  })
  loadRoles()
}

onMounted(loadRoles)

const { currentPage, totalPages, paginatedItems: paginatedRoles } = usePagination(roles)
</script>

<template>
  <div class="admin-roles-page">
    <Toast />

    <div class="page-shell">
      <div class="top-bar">
        <Button
          label="Назад"
          icon="pi pi-arrow-left"
          class="back-btn"
          outlined
          @click="goBack"
        />
      </div>

      <div class="page-header">
        <div class="header-content">
          <div>
            <h1>Роли</h1>
            <p>Управление ролями и их правами доступа</p>
          </div>
          <Button
            label="Создать роль"
            icon="pi pi-plus"
            class="create-btn"
            @click="openCreateRole"
          />
        </div>
      </div>

      <Card class="roles-card">
        <template #content>
          <div v-if="loading" class="state-block">
            <i class="pi pi-spin pi-spinner"></i>
            <span>Загрузка...</span>
          </div>

          <div v-else-if="roles.length === 0" class="state-block">
            <i class="pi pi-inbox"></i>
            <span>Роли не найдены</span>
            <Button
              label="Создать первую роль"
              icon="pi pi-plus"
              class="p-button-text"
              @click="openCreateRole"
            />
          </div>

          <div v-else class="roles-grid">
            <div
              v-for="role in paginatedRoles"
              :key="role.id"
              class="role-card"
            >
              <div class="role-info">
                <div class="role-name">
                  {{ role.name }}
                  <span v-if="!canDeleteRole(role.name)" class="protected-badge">
                    <i class="pi pi-lock"></i> Системная
                  </span>
                </div>
                <div class="role-id">
                  ID: {{ role.id }}
                </div>
              </div>

              <div class="role-actions">
                <Button
                  icon="pi pi-shield"
                  class="permissions-btn"
                  outlined
                  tooltip="Управление правами"
                  @click="openRolePermissions(role.id)"
                />
                <Button
                  icon="pi pi-pencil"
                  class="edit-btn"
                  outlined
                  tooltip="Редактировать роль"
                  @click="openEditRole(role)"
                />
                <Button
                  icon="pi pi-trash"
                  class="delete-btn"
                  outlined
                  severity="danger"
                  tooltip="Удалить роль"
                  :disabled="!canDeleteRole(role.name)"
                  @click="openDeleteDialog(role)"
                />
              </div>
            </div>
          </div>
          <div v-if="totalPages > 1" class="pagination">
            <Button icon="pi pi-chevron-left" outlined :disabled="currentPage === 1" @click="currentPage--" />
            <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
            <Button icon="pi pi-chevron-right" outlined :disabled="currentPage === totalPages" @click="currentPage++" />
          </div>
        </template>
      </Card>
    </div>

    <RoleFormDialog
      :visible="showRoleForm"
      :role="currentRole"
      :mode="formMode"
      @update:visible="showRoleForm = $event"
      @saved="handleRoleSaved"
    />

    <ConfirmDeleteDialog
      :visible="showDeleteDialog"
      :role-name="currentRole?.name || ''"
      :loading="deleteLoading"
      @update:visible="closeDeleteDialog"
      @confirm="handleDeleteRole"
    />
  </div>
</template>

<style scoped>
.admin-roles-page {
  background: #fafafa;
  min-height: 100vh;
}

.page-shell {
  max-width: 1100px;
  margin: 0 auto;
  padding: 32px 24px 48px;
}

.top-bar {
  margin-bottom: 24px;
}

.page-header {
  margin-bottom: 32px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.page-header h1 {
  margin: 0;
  font-size: 36px;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: #111111;
}

.page-header p {
  margin: 10px 0 0;
  font-size: 15px;
  color: #6b6b6b;
}

.create-btn {
  background: #111;
  border: none;
  border-radius: 999px;
  padding: 10px 20px;
}

.create-btn:hover {
  background: #333;
}

.roles-card {
  border: 1px solid #ececec;
  border-radius: 24px;
  box-shadow: 0 18px 40px rgba(17, 17, 17, 0.05);
  overflow: hidden;
}

:deep(.roles-card.p-card) {
  background: #ffffff;
}

:deep(.roles-card .p-card-content) {
  padding: 24px;
}

.roles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 20px 0 8px;
}

.page-info {
  font-size: 14px;
  color: #555;
  min-width: 60px;
  text-align: center;
}

.role-card {
  border: 1px solid #ececec;
  border-radius: 20px;
  padding: 18px;
  background: #ffffff;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.2s ease;
}

.role-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(17, 17, 17, 0.06);
  border-color: #dddddd;
}

.role-info {
  flex: 1;
}

.role-name {
  font-size: 18px;
  font-weight: 600;
  color: #111111;
  word-break: break-word;
}

.protected-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: normal;
  color: #856404;
  background-color: #fff3cd;
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: 8px;
}

.protected-badge i {
  font-size: 10px;
}

.role-id {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.role-actions {
  display: flex;
  gap: 8px;
}

:deep(.permissions-btn.p-button),
:deep(.edit-btn.p-button),
:deep(.delete-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  padding: 8px;
}

:deep(.permissions-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

:deep(.permissions-btn.p-button .p-button-icon) {
  color: #c41217;
}

:deep(.edit-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #2196f3;
  color: #2196f3;
}

:deep(.delete-btn.p-button:hover:not(:disabled)) {
  background: #fee;
  border-color: #e74c3c;
  color: #e74c3c;
}

:deep(.delete-btn.p-button:disabled) {
  opacity: 0.5;
  cursor: not-allowed;
}

:deep(.back-btn.p-button) {
  background: #ffffff;
  border: 1px solid #d8d8d8;
  color: #111111;
  border-radius: 999px;
  box-shadow: none;
}

:deep(.back-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
  color: #111111;
}

.state-block {
  padding: 40px;
  text-align: center;
  border: 1px dashed #d7d7d7;
  border-radius: 18px;
  color: #777;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.state-block i {
  font-size: 48px;
  opacity: 0.5;
}

.state-block .p-button {
  margin-top: 8px;
}

@media (max-width: 760px) {
  .page-shell {
    padding: 24px 16px 40px;
  }

  .header-content {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .page-header h1 {
    font-size: 30px;
  }

  .roles-grid {
    grid-template-columns: 1fr;
  }

  .role-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .role-actions {
    width: 100%;
    justify-content: flex-start;
  }
}

@media (max-width: 480px) {
  .page-shell {
    padding: 16px 12px 32px;
  }

  .page-header h1 {
    font-size: 26px;
  }

  .role-name {
    font-size: 16px;
  }
}
</style>
